import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '@core/services/alert.service';
import { CommonService } from '@core/services/common.service';
import { CorrespondenceService } from '@core/services/correspondence.service';
import { DataService } from '@core/services/data.service';
import { LoadingService } from '@core/services/loading.service';
import { ModalService } from '@core/services/modal.service';
import { PolicyService } from '@core/services/policy.service';
import { TranslateService } from '@ngx-translate/core';
import { PolicyDetail } from '@shared/models/policy-detail';
import { Address } from 'app/models/policy/detail/cancel-policy/Address';
import { AddressResponse } from 'app/models/policy/detail/cancel-policy/AddressResponse';
import { CheckVinRequest } from 'app/models/policy/detail/cancel-policy/check-vin/CheckVinRequest';
import { CheckVinResponse } from 'app/models/policy/detail/cancel-policy/check-vin/CheckVinResponse';
import { PaymForm } from 'app/models/policy/detail/cancel-policy/PaymForm';
import { TerminationRequest, TerminationRequestMotor } from 'app/models/policy/detail/cancel-policy/termination/TerminationRequest';
import { TerminationResponse } from 'app/models/policy/detail/cancel-policy/termination/TerminationResponse';
import { TerminationType } from 'app/models/policy/detail/cancel-policy/TerminationType';
import { PaymentFrequencyCoefficient } from 'app/models/policy/PaymentFrequencyCoefficient';
import moment, { Moment } from 'moment';
import { PolicyDetailComponent } from '../../policy-detail.component';
import { BankAccountValidator, PaymFormValidator } from './cancel-policy-form-validator';
import { MaInsurance, MaInsuranceCar } from '@shared/models/ma-insurance';

@Component({
    selector: 'app-cancel-policy',
    templateUrl: './cancel-policy.component.html',
    styleUrls: ['./cancel-policy.component.scss'],
    standalone: false
})
export class CancelPolicyComponent extends PolicyDetailComponent implements OnInit {

    // Base FormGroup
    public cancelationForm: FormGroup;

    // Incoming data
    private policyDetail: PolicyDetail;
    public terminationTypes: TerminationType[] = [];
    public policiesToCancel: CheckVinResponse[] = [];
    private address: Address;

    // display form parts conditions
    public showOtherPart: boolean = false;

    // other required data
    public paymForm: PaymForm;
    public closestPolicyEndDate: Moment;


    public maInsurance: MaInsuranceCar;


    constructor(
        public commonService: CommonService,
        protected dataService: DataService,
        protected policyService: PolicyService,
        protected route: ActivatedRoute,
        protected correspondenceService: CorrespondenceService,
        protected translateService: TranslateService,
        protected alertService: AlertService,

        public loadingService: LoadingService,
        private modalService: ModalService,
        private router: Router,
    ) {
        super(commonService, dataService, policyService, route, correspondenceService, translateService, alertService, loadingService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.policyService.getPolicyDetail(this.policyState, this.policyNumber).subscribe((policyDetail: PolicyDetail) => {
            this.policyDetail = policyDetail;
            this.maInsurance = policyDetail.maInsurance as MaInsuranceCar;

            this.findClosestPolicyEndDate();

            // level 1 values - termination types
            this.policyService.getTerminationTypes(policyDetail.policy.detailTypeIdentifier, this.policyNumber).subscribe(
                (terminationTypes: TerminationType[]) => {
                    
                    this.terminationTypes = terminationTypes.filter((tt) => {
                        if(this.policyDetail.policy.lineOfBusinessId === 3 && tt.code === 'A081'){
                            return false
                        }
                        return true;
                    });

                    this.policiesToCancel.push({
                        crn: '',
                        vin: '',
                        lobId: this.policyDetail.policy.lineOfBusinessId,
                        paymentBlockageFlag: this.policyDetail.policy.paymentBlockageFlag,
                        policyNumber: this.policyDetail.policy.policyNumber,
                        productNameSpecific: this.policyDetail.policy.productNameSpecific
                    });

                    if (this.policyDetail.policy.detailTypeIdentifier == 'CAR-INSURANCE') {
                        this.policiesToCancel = [];

                        let checkVinRequest = new CheckVinRequest();
                        checkVinRequest.vin = this.maInsurance.vehicle != undefined ?
                            this.maInsurance.vehicle.vin : '';

                        this.policyService.checkVin(checkVinRequest).subscribe(
                            (checkVinResponse: CheckVinResponse[]) => {
                                this.policiesToCancel = checkVinResponse;
                            }
                        );
                    }

                    // level 3 values - refunding
                    this.policyService.getUserAddress().subscribe((addressResponse: AddressResponse) => {

                        this.address = new Address();
                        this.address.name = this.loggedUser.firstName;
                        this.address.surname = this.loggedUser.lastName;
                        this.address.addressResponse = addressResponse;

                        this.paymForm = new PaymForm(policyDetail.policy, this.address);

                        this.initForm();
                    });
                }
            );
        });
    }

    getPoliciesToCancel(): CheckVinResponse[] {

        if(this.policyDetail.policy.lineOfBusinessId === 8 && this.cancelationForm.get('terminationType').value === 'A081'){
            return this.policiesToCancel.filter(x => x.policyNumber == this.policyNumber);
        }

        return this.cancelationForm.get('terminationType').value !== 'B290' ?
            this.policiesToCancel : this.policiesToCancel.filter(x => x.policyNumber == this.policyNumber);
    }


    submit($event: SubmitEvent) {
        if (!this.cancelationForm.valid) {
            return;
        }

        this.modalService.openModal('StornoAlert', this.getPoliciesToCancel()).confirm.subscribe((submitEvent: SubmitEvent) => {
            if (submitEvent == null) {
                return;
            }

            let terminationRequest = new TerminationRequest();
            terminationRequest.policyNumber = this.policyNumber;
            terminationRequest.terminationType = this.terminationTypes
                .find(x => x.code == this.cancelationForm.get('terminationType').value);

            terminationRequest.cancelDate = this.closestPolicyEndDate.format('YYYY-MM-DD');
            terminationRequest.refundType = this.cancelationForm.get('refunding.paymForm').value;

            terminationRequest.iban = this.cancelationForm
                .get('refunding.paymFormOptions.bankAccount.account').value == 'custom' ?
                this.cancelationForm
                    .get('refunding.paymFormOptions.bankAccount.customBankAccount').value :
                this.cancelationForm
                    .get('refunding.paymFormOptions.bankAccount.account').value
                ;

            terminationRequest.fullName = this.address.name + ' ' + this.address.surname;
            terminationRequest.street = this.address.addressResponse.street;
            terminationRequest.orientationNumber =
                (this.address.addressResponse.descriptiveNumber != null) ?
                    this.address.addressResponse.descriptiveNumber :
                    this.address.addressResponse.registrationNumber;

            terminationRequest.city = this.address.addressResponse.city;
            terminationRequest.zip = this.address.addressResponse.zip;
            terminationRequest.countryCode = this.address.addressResponse.state;
            terminationRequest.lobId = this.policyDetail.policy.lineOfBusinessId;
            terminationRequest.paymentBlockageFlag = this.policyDetail.policy.paymentBlockageFlag;


            if (this.policyDetail.policy.detailTypeIdentifier == 'CAR-INSURANCE') {
                let terminationRequestMotor = new TerminationRequestMotor();
                terminationRequestMotor.crn = this.maInsurance.vehicle.crn;
                terminationRequestMotor.vin = this.maInsurance.vehicle.vin;
                terminationRequestMotor.mtplPolicies = this.getPoliciesToCancel();

                terminationRequest.motor = terminationRequestMotor;
            }

            this.policyService.cancelPolicy(terminationRequest)
                .subscribe((terminationResponse: TerminationResponse) => {
                    if (terminationResponse.xmlFiles.length > 0) {
                        this.modalService.openModal('StornoSuccess')
                            .confirm.subscribe((submitEvent: SubmitEvent) => {
                                if (submitEvent != null) {
                                    this.router.navigate(['/moje-poistky',
                                        this.policyDetail.policy.policyState.name,
                                        this.policyDetail.policy.policyNumber
                                    ]);
                                }
                            });
                    }
                });
        });
    }

    resetSelections() {
        this.cancelationForm.get('refunding').reset();
        this.cancelationForm.get('refunding.paymFormOptions.address')
            .setValue(this.paymForm.getChoices('ADDRESS'));
    }

    resetRefundType() {
        this.cancelationForm.get('refunding.paymFormOptions.bankAccount').reset();
        this.cancelationForm.get('refunding.paymFormOptions.address')
            .setValue(this.paymForm.getChoices('ADDRESS'));
        this.resetIBAN();
    }

    resetIBAN() {
        if (this.cancelationForm.get('refunding.paymFormOptions.bankAccount').value != 'custom') {
            this.cancelationForm
                .get('refunding.paymFormOptions.bankAccount.customBankAccount').reset();
        }

        this.cancelationForm
            .get('refunding.paymFormOptions.bankAccount.customBankAccount')
            .setErrors(null);
    }

    /**
     * PRIVATE
     */
    private initForm() {

        (this.cancelationForm = new FormGroup(
            {
                terminationType: new FormControl(
                    this.policyDetail.policy.lineOfBusinessId == 9 ? this.terminationTypes[0].code : null
                    , Validators.required),
                cancelationDate: new FormControl(this.closestPolicyEndDate.format('DD.MM.YYYY')),
                refunding: new FormGroup({
                    paymForm: new FormControl(null, Validators.required),
                    paymFormOptions: new FormGroup({
                        address: new FormControl(null),
                        bankAccount: new FormGroup({
                            account: new FormControl(null, BankAccountValidator()),
                            customBankAccount: new FormControl(null)
                        })
                    })
                }, PaymFormValidator()),
            }
        )).valueChanges.subscribe(values => {
            this.showOtherPart = values.terminationType != null;
        });

        this.showOtherPart = this.cancelationForm.value.terminationType != null;
    }

    /**
     * Closest Policy End Date Algorithm
     */
    private findClosestPolicyEndDate() {
        /**
         * Z beginDate zoberieme zložku DD.MM a pridáme aktuálny kalendárny rok YYYY
         */
        let policyBeginDate = (this.policyNumber.startsWith('706') || this.policyNumber.startsWith('806')) ? moment(this.policyDetail.policy.dateBeginOrig).set('year', moment().year()) : moment(this.policyDetail.policy.beginDate).set('year', moment().year());

        /**
         * V prípade, že policyBeginDate > sysdate prestavíme dátum o rok skôr
         * Najnižšia porovnávaná granularita sú dni
         */
        if (moment(policyBeginDate).isAfter(moment.now(), 'days')) {
            policyBeginDate.set('year', moment().year() - 1);
        }

        /**
         * Koeficienty odpovedajúce jednotlivým lehotám platenia
         */
        let paymentFrequencyCoefficient = (new PaymentFrequencyCoefficient())
            .getCoefficient(this.policyDetail.policy.paymentFrequencyId);

        if ((!moment(moment.now()).isBefore(moment(policyBeginDate).subtract(6, 'weeks')) && this.policyDetail.policy.paymentFrequencyId == 2) || this.policyDetail.policy.lineOfBusinessId == 8) {
            paymentFrequencyCoefficient = 12;
        }

        let month = 0;
        let maxIterationMonths = 1000;
        let result = false;
        let lehotnyDen: Moment;
        while (!result) {
            let addition = (month + 1) * paymentFrequencyCoefficient;
            let pbd = policyBeginDate.clone();
            lehotnyDen = pbd.add(addition, 'months');

            if( Math.ceil(
                moment.duration(
                    lehotnyDen.subtract(1, 'day').endOf('day')
                    .diff(moment(moment(), 'DD.MM.YYYY').startOf('day'))
                ).asDays()) >= 42 ) {
                result = true;
            }
            month++;

            // protection
            if (month >= maxIterationMonths) {
                lehotnyDen = null;
                result = true;
            }
        }

        this.closestPolicyEndDate = lehotnyDen != null ? lehotnyDen : null;
    }
}
